import { chunk, range } from "lodash-es";

import { snakeDraft } from "./snake-draft";

// Calculates the finish for a round. Each team that finishes in the same round tie.
//
// - Losers of round of 16 all tie for 9th because there are 8 teams that make it further than them
// - Loser's of quarters tie for 5th because 4 teams make it further
// - etc
//
// The math doesn't work for finals, those are set manually.
export function getFinishForRound(round: number) {
  return 2 ** round + 1;
}

// Default playoff bracket seeding, transcribed from the TD's authoritative
// "Playoff Seeding — funky 4/8/12 pools" spreadsheet (see issue #592). Each
// array is indexed by overall bracket seed (index 0 = bracket seed 1); each
// entry maps that bracket slot to a pool's finisher: `pool` is 0-based, `seed`
// is 1-based place (1st/2nd/3rd). Invariant from the spreadsheet: a pool's 1st
// and 2nd land in opposite bracket halves, so they can only meet in the final.
//
// Tables cover 1st/2nd/3rd only. Deeper pools (4+ advancing) fall through to
// fancySnakeDraft via seedPlayoffs. Pool counts 9/10/11 aren't listed here —
// their fancySnakeDraft output already matches the spreadsheet.
const SEED_ORDERS: { [key: number]: { pool: number; seed: number }[] } = {
  2: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 0, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 0, seed: 3 },
    { pool: 1, seed: 3 },
  ],
  3: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 2, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 0, seed: 2 },
  ],
  4: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 3, seed: 1 },
    { pool: 2, seed: 2 },
    { pool: 3, seed: 2 },
    { pool: 0, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 2, seed: 3 },
    { pool: 3, seed: 3 },
    { pool: 0, seed: 3 },
    { pool: 1, seed: 3 },
  ],
  5: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 3, seed: 1 },
    { pool: 4, seed: 1 },
    { pool: 4, seed: 2 },
    { pool: 3, seed: 2 },
    { pool: 2, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 0, seed: 2 },
    { pool: 0, seed: 3 },
    { pool: 1, seed: 3 },
    { pool: 2, seed: 3 },
    { pool: 3, seed: 3 },
    { pool: 4, seed: 3 },
  ],
  // 12 teams render into a 16-slot bracket, so a pool's 1st and 2nd seeds
  // must be paired to land in opposite halves of that frame (not paired by
  // a 12-slot complement). Second-place seeds: pool1->11, pool2->12,
  // pool3->9, pool4->10, pool5->7, pool6->8 — matching the printed super
  // snaker chart. See the "pool partners land in opposite halves" test.
  6: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 3, seed: 1 },
    { pool: 4, seed: 1 },
    { pool: 5, seed: 1 },
    { pool: 4, seed: 2 },
    { pool: 5, seed: 2 },
    { pool: 2, seed: 2 },
    { pool: 3, seed: 2 },
    { pool: 0, seed: 2 },
    { pool: 1, seed: 2 },
  ],
  7: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 3, seed: 1 },
    { pool: 4, seed: 1 },
    { pool: 5, seed: 1 },
    { pool: 6, seed: 1 },
    { pool: 6, seed: 2 },
    { pool: 5, seed: 2 },
    { pool: 4, seed: 2 },
    { pool: 3, seed: 2 },
    { pool: 2, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 0, seed: 2 },
  ],
  8: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 3, seed: 1 },
    { pool: 4, seed: 1 },
    { pool: 5, seed: 1 },
    { pool: 6, seed: 1 },
    { pool: 7, seed: 1 },
    { pool: 6, seed: 2 },
    { pool: 7, seed: 2 },
    { pool: 4, seed: 2 },
    { pool: 5, seed: 2 },
    { pool: 2, seed: 2 },
    { pool: 3, seed: 2 },
    { pool: 0, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 2, seed: 3 },
    { pool: 3, seed: 3 },
    { pool: 0, seed: 3 },
    { pool: 1, seed: 3 },
    { pool: 6, seed: 3 },
    { pool: 7, seed: 3 },
    { pool: 4, seed: 3 },
    { pool: 5, seed: 3 },
  ],
  12: [
    { pool: 0, seed: 1 },
    { pool: 1, seed: 1 },
    { pool: 2, seed: 1 },
    { pool: 3, seed: 1 },
    { pool: 4, seed: 1 },
    { pool: 5, seed: 1 },
    { pool: 6, seed: 1 },
    { pool: 7, seed: 1 },
    { pool: 8, seed: 1 },
    { pool: 9, seed: 1 },
    { pool: 10, seed: 1 },
    { pool: 11, seed: 1 },
    { pool: 10, seed: 2 },
    { pool: 11, seed: 2 },
    { pool: 8, seed: 2 },
    { pool: 9, seed: 2 },
    { pool: 6, seed: 2 },
    { pool: 7, seed: 2 },
    { pool: 4, seed: 2 },
    { pool: 5, seed: 2 },
    { pool: 2, seed: 2 },
    { pool: 3, seed: 2 },
    { pool: 0, seed: 2 },
    { pool: 1, seed: 2 },
    { pool: 2, seed: 3 },
    { pool: 3, seed: 3 },
    { pool: 0, seed: 3 },
    { pool: 1, seed: 3 },
    { pool: 6, seed: 3 },
    { pool: 7, seed: 3 },
    { pool: 4, seed: 3 },
    { pool: 5, seed: 3 },
    { pool: 10, seed: 3 },
    { pool: 11, seed: 3 },
    { pool: 8, seed: 3 },
    { pool: 9, seed: 3 },
  ],
};

export function seedPlayoffs(
  size: number,
  poolCount: number,
): { pool: number; seed: number }[] {
  const seedOrders = SEED_ORDERS[poolCount];

  if (!seedOrders) {
    return fancySnakeDraft(size, poolCount);
  }

  if (seedOrders.length >= size) {
    return seedOrders;
  }

  // Hardcoded seed orders don't cover enough teams (e.g. 3 teams per pool
  // when the seed order only defines 2). Use the curated orders for the
  // rounds they cover, then append remaining rounds from fancySnakeDraft.
  const fullDraft = fancySnakeDraft(size, poolCount);
  return [...seedOrders, ...fullDraft.slice(seedOrders.length)];
}

export function fancySnakeDraft(
  size: number,
  poolCount: number,
): { pool: number; seed: number }[] {
  // Determine how many rounds of teams per pool we need.
  // Use ceil so that when size isn't evenly divisible by poolCount,
  // we still generate enough entries (e.g. 16 teams / 6 pools needs
  // 3rd-place finishers from some pools).
  const teamsPerPool = Math.ceil(size / poolCount);

  return Array.from({ length: teamsPerPool })
    .flatMap((_, i) => {
      let pools: number[] = Array.from({ length: poolCount }).map((_, i) => i);

      if (i % 2 === 1) {
        pools.reverse();
      }

      if (poolCount % 2 === 0 && i > 0) {
        if (i % 2 === 1) {
          pools = chunk(pools, 2).flatMap(([a, b]) => [b, a]);
        }

        if ((i === 3 || i % 2 === 0) && poolCount % 4 === 0) {
          pools = chunk(pools, 4).flatMap(([a, b, c, d]) => [d, c, b, a]);
        }
      }

      return pools.map((p) => ({ pool: p, seed: i + 1 }));
    })
    .slice(0, size);
}

export function snakePlayoffs(
  size: number,
  pools: number[],
): { poolId: number; seed: number }[] {
  const pc = pools.length;
  const result: { poolId: number; seed: number }[] = [];

  for (let i = 0; i < size / pc; i++) {
    const currentPools = [...pools]; // Create a copy

    // Reverse order for odd rounds (snake pattern)
    if (i % 2 === 1) {
      currentPools.reverse();
    }

    // Additional adjustments for even number of pools
    if (pc % 2 === 0 && i > 0) {
      if (i % 2 === 1) {
        // Reverse pairs of pools
        for (let j = 0; j < currentPools.length; j += 2) {
          if (j + 1 < currentPools.length) {
            [currentPools[j], currentPools[j + 1]] = [
              currentPools[j + 1],
              currentPools[j],
            ];
          }
        }
      }

      // Quad swapping for certain rounds
      if ((i === 3 || i % 2 === 0) && pc % 4 === 0) {
        // Reverse groups of 4 pools
        for (let j = 0; j < currentPools.length; j += 4) {
          if (j + 3 < currentPools.length) {
            [currentPools[j], currentPools[j + 3]] = [
              currentPools[j + 3],
              currentPools[j],
            ];
            [currentPools[j + 1], currentPools[j + 2]] = [
              currentPools[j + 2],
              currentPools[j + 1],
            ];
          }
        }
      }
    }

    // Add teams from this round to result
    for (const pool of currentPools) {
      result.push({ poolId: pool, seed: i + 1 });
    }
  }

  return result;
}

export function recursiveSnakeDraft(seeds: number[]): number[][] {
  if (seeds.length === 2) {
    // Base case: just return the pair with even seed in second slot
    const [a, b] = seeds;

    return [a % 2 === 0 ? [b, a] : [a, b]];
  }

  // Perform snake draft to split into two chunks
  const [chunk1, chunk2] = snakeDraft(seeds, 2);

  // Recursively process each chunk
  const result1 = recursiveSnakeDraft(chunk1);
  const result2 = recursiveSnakeDraft(chunk2);

  result2.reverse();

  // Combine results
  return [...result1, ...result2];
}

export type PlayoffMatchNode = {
  aSeed?: number | null;
  bSeed?: number | null;
  aFrom?: number | null;
  bFrom?: number | null;
};

export function draftPlayoffs(size: number) {
  const seedCount = 2 ** Math.ceil(Math.log2(size));

  const firstRound = recursiveSnakeDraft(range(1, seedCount + 1)) as [
    number,
    number,
  ][];

  const rounds: (PlayoffMatchNode | null)[][] = [
    firstRound.map(([aSeed, bSeed]) => ({ aSeed, bSeed })),
  ];

  for (let i = 1; i < Math.log2(seedCount); i++) {
    rounds[i] = [];

    const prev = rounds[i - 1];

    for (let j = 0; j < prev.length; j += 2) {
      const next: PlayoffMatchNode = {};

      const aMatch = rounds[i - 1][j];

      if (
        aMatch?.aSeed &&
        aMatch.bSeed &&
        (aMatch.aSeed > size || aMatch.bSeed > size)
      ) {
        next.aSeed = aMatch.aSeed < size ? aMatch.aSeed : aMatch.bSeed;

        rounds[i - 1][j] = null;
      } else {
        next.aFrom = j;
      }

      const bMatch = rounds[i - 1][j + 1];

      if (
        bMatch?.aSeed &&
        bMatch.bSeed &&
        (bMatch.aSeed > size || bMatch.bSeed > size)
      ) {
        next.bSeed = bMatch.aSeed < size ? bMatch.aSeed : bMatch.bSeed;

        rounds[i - 1][j + 1] = null;
      } else {
        next.bFrom = j + 1;
      }

      rounds[i].push(next);
    }
  }

  return rounds;
}
